import { useCallback, useSyncExternalStore } from 'react';

import axiosClient from '../../api/axios-client';
import { useStateContext } from '../../contexts/ContextProvider';
import { detachCurrentPushSubscription } from '../../helpers/webPushSubscription';
import { viderTousLesBrouillons } from '../hooks/useBrouillonPersistant';
import { deconnecterTousLesAppareils } from './usePreferencesNotifications';

/**
 * Le secours quand le serveur n'a pas pu détacher le push — audit 2026-09-23 (A4).
 *
 * `detachCurrentPushSubscription` fait le DELETE serveur AVANT `unsubscribe()` :
 * s'il échoue, le navigateur reste abonné et le téléphone déconnecté continue de
 * sonner. On coupe donc au moins de ce côté-ci. Le helper, partagé avec le web,
 * n'est pas touché ; le serveur purgera l'endpoint mort à son premier 410.
 */
async function desabonnerCeTelephone(): Promise<void> {
    try {
        if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
        const enregistrement = await navigator.serviceWorker.getRegistration();
        const abonnement = await enregistrement?.pushManager.getSubscription();
        await abonnement?.unsubscribe();
    } catch {
        /* meilleur effort */
    }
}

/*
 * Le verrou, UN pour toute l'app (relecture M2). Quatre écrans montent chacun
 * leur `useDeconnexion` : un verrou par instance laissait partir ensemble la
 * déconnexion du panneau et « tous mes appareils ». Chaque instance lit le même
 * état via `useSyncExternalStore`, et ses boutons se désactivent ensemble.
 */
let verrouille = false;
const abonnes = new Set<() => void>();

function poserVerrou(valeur: boolean): void {
    verrouille = valeur;
    abonnes.forEach((prevenir) => prevenir());
}

function sAbonner(prevenir: () => void): () => void {
    abonnes.add(prevenir);
    return () => {
        abonnes.delete(prevenir);
    };
}

const lireVerrou = () => verrouille;

/** Réservé aux tests : le verrou vit dans le module, il survit d'un test à l'autre. */
export function reinitialiserVerrouDeconnexion(): void {
    poserVerrou(false);
}

/**
 * La déconnexion, une seule fois pour toute l'app — audit 2026-09-23.
 *
 * Trois endroits se déconnectaient ; aucun ne révoquait la session (le jeton restait
 * valide 30 jours glissants), `FinDeCourse` ne coupait même pas le push. Ordre :
 * 1. détacher le push (#78) — la requête a besoin du jeton ;
 * 2. `POST /logout`, comme le web (`api/useLogout.ts`) ;
 * 3. effacer les brouillons ; 4. vider le jeton.
 * Les étapes réseau sont en meilleur effort : une déconnexion ne se retient jamais.
 *
 * Sur un réseau captif, `/logout` peut mettre ~30 s : `enCours` le dit aux boutons,
 * et un second appel pendant le premier est ignoré (relecture M3).
 */
export function useDeconnexion() {
    const { setToken } = useStateContext();
    const enCours = useSyncExternalStore(sAbonner, lireVerrou, lireVerrou);

    const seDeconnecter = useCallback(async () => {
        if (verrouille) return;
        poserVerrou(true);
        try {
            try {
                await detachCurrentPushSubscription();
            } catch {
                // Sans attendre : une déconnexion ne se retient jamais (A4).
                void desabonnerCeTelephone();
            }
            await axiosClient.post('/logout').catch(() => undefined);
            viderTousLesBrouillons();
            setToken(null);
        } finally {
            poserVerrou(false);
        }
    }, [setToken]);

    /*
     * « Déconnecter tous mes appareils » — audit 2026-09-23 (A5). Même verrou que
     * `seDeconnecter` : un double tap, ou les deux boutons à la suite, ne lancent
     * qu'une déconnexion. Contrairement à elle, l'échec REMONTE : les appareils
     * sont toujours connectés, rien n'est vidé et l'écran doit le dire.
     *
     * Le verrou n'est rendu QU'EN CAS D'ÉCHEC (relecture M4) : au succès la page se
     * recharge, et un bouton réactivé entre-temps inviterait à un second appel.
     */
    const seDeconnecterPartout = useCallback(async () => {
        if (verrouille) return;
        poserVerrou(true);
        try {
            await deconnecterTousLesAppareils();
        } catch (erreur) {
            poserVerrou(false);
            throw erreur;
        }
        viderTousLesBrouillons();
        window.location.reload();
    }, []);

    return { seDeconnecter, seDeconnecterPartout, enCours };
}
